/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import Render from '../../render/Render';
import PulseLoader from '../loading/PulseLoader';
import {Option} from './AutocompleteSearch';
import SearchOverlayRow from './SearchOverlayRow';
import {getSearchOverlayOptionKey} from './SearchOverlayUtils';

export interface SearchOverlayPanelProps {
  classes: any;
  displayResults: Option[];
  hasResults: boolean;
  onRecentSearchRemove?: (option: Option) => void;
  onResultClick: (option: Option) => void;
  panelTitle: string;
  resultCounter: string;
  searchLoading: boolean;
  searchValue: string;
  stateText: string;
}

/**
 * Renders the tracking search overlay result panel.
 *
 * ```typescript
 * import SearchOverlayPanel from '@ion/dls/src/components/search/SearchOverlayPanel';
 *
 * ...
 *
 * <SearchOverlayPanel classes={classes}
 *   displayResults={displayResults}
 *   hasResults={hasResults}
 *   searchValue={searchValue}
 *   onResultClick={selectResult} />
 * ```
 *
 * @param props Defines result state, loading state, and callbacks.
 * @returns The search result or recent-search panel.
 */
export const SearchOverlayPanel = ({
  classes,
  displayResults,
  hasResults,
  onRecentSearchRemove,
  onResultClick,
  panelTitle,
  resultCounter,
  searchLoading,
  searchValue,
  stateText
}: SearchOverlayPanelProps) => (
  <div className={classes.panel}>
    <div className={classes.panelHeader}>
      <span>{panelTitle}</span>
      <Render.If condition={!!searchValue && !searchLoading && !!resultCounter}>
        <span className={classes.resultCount}>{resultCounter}</span>
      </Render.If>
    </div>
    <div className={classes.list}>
      <Render.If condition={searchLoading}>
        <div className={classes.loader}>
          <PulseLoader message={translate('common:search.loadingTextDefault')} />
        </div>
      </Render.If>
      <Render.If condition={hasResults}>
        {displayResults.map((option, index) => (
          <SearchOverlayRow key={getSearchOverlayOptionKey(option, index)}
            classes={classes}
            option={option}
            searchValue={searchValue}
            onResultClick={onResultClick}
            onRecentSearchRemove={onRecentSearchRemove} />
        ))}
      </Render.If>
      <Render.If condition={!searchLoading && !hasResults}>
        <div className={classes.stateText}>{stateText}</div>
      </Render.If>
    </div>
  </div>
);

export default memo(SearchOverlayPanel);
